import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./placePage.css";
import {
  getCountries,
  getCitiesByCountryId,
  getTopDestinations,
  getPlacesWithImage,
} from "../../api";

const PAGE_SIZE = 9;

const PlacePage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // Data
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [places, setPlaces] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters (UI values)
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [priceLevel, setPriceLevel] = useState("");

  // Filters actually applied to API
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    countryID: "",
    cityID: "",
    categoryID: "",
    priceLevel: "",
  });

  // UI state
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  // On mount, check navigation state for city/country
  useEffect(() => {
    if (location.state?.countryID) {
      setSelectedCountry(location.state.countryID);
      setAppliedFilters((prev) => ({
        ...prev,
        countryID: location.state.countryID,
      }));
    }
    if (location.state?.cityID) {
      setSelectedCity(location.state.cityID);
      setAppliedFilters((prev) => ({
        ...prev,
        cityID: location.state.cityID,
      }));
    }
  }, [location.state]);

  // Read ?q= from URL on first load and apply to search filter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q") || "";
    if (q) {
      setSearch(q);
      setAppliedFilters((prev) => ({
        ...prev,
        search: q,
      }));
      setCurrentPage(1);
    }
    // Also handle country/city from query params
    const countryID = params.get("country");
    const cityID = params.get("city");
    if (countryID) {
      setSelectedCountry(countryID);
      setAppliedFilters((prev) => ({
        ...prev,
        countryID,
      }));
    }
    if (cityID) {
      setSelectedCity(cityID);
      setAppliedFilters((prev) => ({
        ...prev,
        cityID,
      }));
    }
  }, [location.search]);

  // Initial: load countries + categories
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingFilters(true);
        const [countriesRes, categoriesRes] = await Promise.all([
          getCountries(),
          getTopDestinations(),
        ]);

        setCountries(countriesRes || []);
        setCategories(categoriesRes || []);
      } catch (err) {
        console.error("Failed to load initial data:", err);
        setError(t("Failed to load filters. Please try again.", "Failed to load filters. Please try again."));
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchInitialData();
  }, [t]);

  // Load cities when selectedCountry changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedCountry) {
        setCities([]);
        setSelectedCity("");
        return;
      }
      try {
        const data = await getCitiesByCountryId(selectedCountry);
        setCities(data || []);
        // Only reset selectedCity if it doesn't match any city in the new list
        if (!data?.some(city => city.CityID === selectedCity)) {
          setSelectedCity("");
        }
      } catch (err) {
        console.error("Failed to load cities:", err);
      }
    };

    fetchCities();
  }, [selectedCountry, selectedCity]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cityID = params.get("city") || location.state?.cityID;
    if (cities.length > 0 && cityID) {
      // Only set if cityID exists in the cities list
      if (cities.some(city => city.CityID === cityID)) {
        setSelectedCity(cityID);
        setAppliedFilters((prev) => ({
          ...prev,
          cityID,
        }));
      }
    }
  }, [cities, location.search, location.state]);

  // Fetch places whenever applied filters or page change
  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        setLoadingPlaces(true);
        setPlaces([]);
        const data = await getPlacesWithImage({
          search: appliedFilters.search,
          categoryID: appliedFilters.categoryID,
          countryID: appliedFilters.countryID,
          cityID: appliedFilters.cityID,
          priceLevel: appliedFilters.priceLevel,
          page: currentPage,
          pageSize: PAGE_SIZE,
          nocache: Date.now(),
        });

        const items =
          data.items ||
          data.places ||
          data.data ||
          (Array.isArray(data) ? data : []);

        // Use pagination.total from API response
        const total = data.pagination?.total || items.length || 0;

        setPlaces(items || []);
        setTotalCount(total);
        setTotalPages(Math.ceil(total / PAGE_SIZE));
      } catch (err) {
        console.error(err);
        setPlaces([]);
      } finally {
        setLoadingPlaces(false);
      }
    };

    fetchPlaces();
  }, [appliedFilters, currentPage]);

  // Handlers
  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setSelectedCity("");
    setCurrentPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      countryID: e.target.value,
      cityID: "",
    }));
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      cityID: e.target.value,
    }));
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      categoryID: e.target.value,
    }));
  };

  const handlePriceChange = (e) => {
    setPriceLevel(e.target.value);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      priceLevel: e.target.value,
    }));
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      search: e.target.value,
    }));
  };

  const handleResetFilters = () => {
    setSearch("");
    setSelectedCountry("");
    setSelectedCity("");
    setSelectedCategory("");
    setPriceLevel("");
    setCurrentPage(1);
    setAppliedFilters({
      search: "",
      countryID: "",
      cityID: "",
      categoryID: "",
      priceLevel: "",
    });
  };

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage((p) => p - 1);
      window.scrollTo({ top: 250, behavior: "smooth" });
    }
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
      window.scrollTo({ top: 250, behavior: "smooth" });
    }
  };

  const handlePlaceClick = (placeID) => {
    navigate(`/place-details/${placeID}`);
  };

  const renderPriceLevel = (level) => {
    const num = Number(level) || 0;
    if (!num) return null;
    return "$".repeat(num > 4 ? 4 : num);
  };

  const skeletonArray = Array.from({ length: 6 });

  return (
    <main className={`page container ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      <div className="places-page fade-up">
        <section className="places-filters">
          <div className="filters-header">
            <h2>{t("Discover places", "Discover places")}</h2>
            <p className="muted">
              {t("Filter by country, city, category and price to find the best spots.", "Filter by country, city, category and price to find the best spots.")}
            </p>
          </div>

          <div className="filters-grid">
            {/* Search */}
            <div className="filter-group">
              <label htmlFor="search">{t("Search places", "Search places")}</label>
              <input
                id="search"
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={t("Search by name or description", "Search by name or description")}
              />
            </div>

            {/* Country */}
            <div className="filter-group">
              <label htmlFor="country">{t("Country", "Country")}</label>
              <select
                id="country"
                value={selectedCountry}
                onChange={handleCountryChange}
              >
                <option value="">{t("All countries", "All countries")}</option>
                {countries.map((country) => (
                  <option key={country.CountryID} value={country.CountryID}>
                    {country.CountryName}
                  </option>
                ))}
              </select>
            </div>

            {/* City */}
            <div className="filter-group">
              <label htmlFor="city">{t("City", "City")}</label>
              <select
                id="city"
                value={selectedCity}
                onChange={handleCityChange}
                disabled={!selectedCountry}
              >
                <option value="">{t("All cities", "All cities")}</option>
                {cities.map((city) => (
                  <option key={city.CityID} value={city.CityID}>
                    {city.CityName}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div className="filter-group">
              <label htmlFor="category">{t("Category", "Category")}</label>
              <select
                id="category"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <option value="">{t("All categories", "All categories")}</option>
                {categories.map((cat) => (
                  <option key={cat.CategoryID} value={cat.CategoryID}>
                    {cat.NameCategory}
                  </option>
                ))}
              </select>
            </div>

            {/* Price level */}
            <div className="filter-group">
              <label htmlFor="price">{t("Price level", "Price level")}</label>
              <select
                id="price"
                value={priceLevel}
                onChange={handlePriceChange}
              >
                <option value="">{t("All price levels", "All price levels")}</option>
                <option value="1">$ {t("Budget", "Budget")}</option>
                <option value="2">$$ {t("Medium", "Medium")}</option>
                <option value="3">$$$ {t("Premium", "Premium")}</option>
                <option value="4">$$$$ {t("Luxury", "Luxury")}</option>
              </select>
            </div>

            {/* Buttons */}
            <div className="filter-actions">
              <button
                type="button"
                className="btn btn-reset"
                onClick={handleResetFilters}
              >
                {t("Reset", "Reset")}
              </button>
            </div>
          </div>

          {loadingFilters && (
            <p className="muted" style={{ marginTop: 8 }}>
              {t("Loading filters…", "Loading filters…")}
            </p>
          )}
        </section>

        {/* Error message */}
        {error && (
          <div className="places-error">
            {error}
          </div>
        )}

        {/* Result header */}
        {!loadingPlaces && places.length > 0 && (
          <div className="places-result-header">
            <span>
              {t("Showing", "Showing")} <strong>{places.length}</strong>
              {totalCount ? (
                <>
                  {" "}
                  {t("result(s) out of", "result(s) out of")} <strong>{totalCount}</strong>
                </>
              ) : null}
            </span>
          </div>
        )}

        {/* Places grid */}
        <section className="places-grid-section">
          {loadingPlaces ? (
            <div className="places-grid">
              {skeletonArray.map((_, idx) => (
                <div
                  key={idx}
                  className="place-card skeleton"
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div className="skeleton-thumb shimmer" />
                  <div className="skeleton-line shimmer" />
                  <div className="skeleton-line short shimmer" />
                  <div className="skeleton-tags shimmer" />
                </div>
              ))}
            </div>
          ) : places.length === 0 ? (
            <div className="places-empty">
              <h3>{t("No places found.", "No places found.")}</h3>
              <p className="muted">
                {t("There are no places for your current filters.", "There are no places for your current filters.")}
              </p>
              <button
                type="button"
                className="btn"
                onClick={handleResetFilters}
              >
                {t("Reset filters", "Reset filters")}
              </button>
            </div>
          ) : (
            <div className="places-grid">
              {places.map((place, index) => (
                <article
                  key={place.PlaceID}
                  className="place-card"
                  style={{ animationDelay: `${index * 60}ms` }}
                  onClick={() => handlePlaceClick(place.PlaceID)}
                >
                  <div className="place-thumb-wrapper">
                    <img
                      src={place.Image || place.MainImageUrl}
                      alt={place.PlaceName}
                      className="place-image"
                    />
                    {place.PriceLevel && (
                      <span className="price-pill">
                        {renderPriceLevel(place.PriceLevel)}
                      </span>
                    )}
                  </div>

                  <div className="place-card-body">
                    <h3 className="place-title">{place.PlaceName}</h3>
                    <div className="place-location">
                      {place.CityName && place.CountryName
                        ? `${place.CityName}, ${place.CountryName}`
                        : place.CountryName || place.CityName || ""}
                    </div>
                    <div className="place-tags">
                      {place.CategoryName && (
                        <span className="tag-soft">
                          {place.CategoryName}
                        </span>
                      )}
                    </div>
                    {place.ShortDescription && (
                      <p className="place-description">
                        {place.ShortDescription}
                      </p>
                    )}
                  </div>
                  <div className="place-card-footer">
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlaceClick(place.PlaceID);
                      }}
                    >
                      {t("View details", "View details")}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="places-pagination">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={currentPage === 1}
              onClick={() => handlePageChange("prev")}
            >
              ‹ {t("Previous", "Previous")}
            </button>
            <span className="page-indicator">
              {t("Page", "Page")} <strong>{currentPage}</strong> {t("of", "of")}{" "}
              <strong>{totalPages}</strong>
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange("next")}
            >
              {t("Next", "Next")} ›
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default PlacePage;